import { useState, useEffect } from "react";
import Head from "next/head";
import dynamic from "next/dynamic";
import { faqItems } from "@/components/pricing/pricingData";
import { ServiceSchema, FAQSchema } from "@/components/SEOSchema";
import { SITE_URL } from "@/lib/constants";

const NewHeader = dynamic(() => import("@/components/NewHeader"), {
  ssr: true,
});
const PricingHero = dynamic(
  () => import("@/components/pricing/PricingHero"),
  { ssr: true }
);
const RevenueSlider = dynamic(
  () => import("@/components/pricing/RevenueSlider"),
  { ssr: true }
);
const PricingCards = dynamic(
  () => import("@/components/pricing/PricingCards"),
  { ssr: true }
);
const PricingAddons = dynamic(
  () => import("@/components/pricing/PricingAddons"),
  { ssr: true }
);
const PricingCompare = dynamic(
  () => import("@/components/pricing/PricingCompare"),
  { ssr: true }
);
const SavingsCalculator = dynamic(
  () => import("@/components/pricing/SavingsCalculator"),
  { ssr: true }
);
const PricingTestimonials = dynamic(
  () => import("@/components/pricing/PricingTestimonials"),
  { ssr: true }
);
const ComparisonFAQ = dynamic(
  () => import("@/components/comparison/ComparisonFAQ"),
  { ssr: true }
);
const CallToAction = dynamic(
  () =>
    import("@/components/homepages/CallToAction").then(
      (mod) => mod.CallToAction
    ),
  { ssr: true }
);
const Footer = dynamic(() => import("@/components/homepages/Footer"), {
  ssr: true,
});

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [revenueTier, setRevenueTier] = useState(1);
  const [annualSavings, setAnnualSavings] = useState(0);

  // Scroll-triggered fade-in animations
  useEffect(() => {
    if (typeof window === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".fade-in:not(.visible)").forEach((el) => io.observe(el));

    const mo = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.classList?.contains("fade-in") && !node.classList.contains("visible")) {
            io.observe(node);
          }
          node.querySelectorAll?.(".fade-in:not(.visible)").forEach((el) => io.observe(el));
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);

  return (
    <>
      <Head>
        <title>Pricing | Fixed Fee AI Bookkeeping Australia - Ledgerowl</title>
        <meta
          name="description"
          content="Transparent fixed fee bookkeeping plans for Australian businesses. AI-powered accounting with BAS lodgement, from $199/mo. Compare Essentials vs Complete."
        />
        <meta
          name="keywords"
          content="AI bookkeeping pricing Australia, fixed fee bookkeeping Australia, automated BAS lodgement, small business accounting plans, AI accounting service Australia, bookkeeping service with accountant Australia"
        />
        <meta property="og:title" content="Pricing | Fixed Fee AI Bookkeeping Australia - Ledgerowl" />
        <meta property="og:description" content="Transparent fixed fee bookkeeping plans for Australian businesses. AI-powered accounting with BAS lodgement, from $199/mo." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/pricing`} />
        <meta property="og:image" content="https://ledgerowl.com.au/favicon.png" />
        <meta property="og:site_name" content="Ledgerowl" />
        <meta property="og:locale" content="en_AU" />
        <link rel="canonical" href={`${SITE_URL}/pricing`} />
      </Head>

      <ServiceSchema
        services={[
          {
            name: "AI Bookkeeping Essentials",
            description:
              "AI-powered bookkeeping and reconciliation with dedicated accountant review, P&L, cash flow, and balance sheet reports. Perfect for Australian small businesses.",
            price: "199",
          },
          {
            name: "AI Bookkeeping Complete",
            description:
              "Full-service AI accounting with BAS preparation & lodgement, ATO compliance, tax return preparation, and monthly review meetings. Everything your Australian business needs.",
            price: "499",
          },
        ]}
      />
      <FAQSchema items={faqItems} />

      <NewHeader />

      <PricingHero billingCycle={billingCycle} onToggle={setBillingCycle} />
      <SavingsCalculator onSavingsChange={setAnnualSavings} revenueTier={revenueTier} />
      {/* <RevenueSlider value={revenueTier} onChange={setRevenueTier} annualSavings={annualSavings} /> */}
      <PricingCards billingCycle={billingCycle} revenueTier={revenueTier} />
      <PricingAddons />
      <PricingCompare />
      <ComparisonFAQ
        title="FAQs"
        subtitle="Everything you need to know about switching from outsourced bookkeeping to Ledgerowl"
        items={faqItems}
      />
      <CallToAction />
      <Footer />
    </>
  );
}
